import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, X, Send, Copy, ExternalLink, DollarSign, Trash2, FileText, RefreshCw } from "lucide-react";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ collected: 0, outstanding: 0 });
  const [loadingList, setLoadingList] = useState(false);
  const [creating, setCreating] = useState(false);

  // form state
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [memo, setMemo] = useState("");
  const [daysDue, setDaysDue] = useState(7);
  const [items, setItems] = useState([{ description: "", amount_dollars: "", quantity: 1 }]);

  const load = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/invoices");
      setInvoices(data.results);
      setTotals({
        collected: data.total_collected_cents,
        outstanding: data.total_outstanding_cents,
      });
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoadingList(false); }
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setItems((prev) => [...prev, { description: "", amount_dollars: "", quantity: 1 }]);
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const updateItem = (i, key, val) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [key]: val } : it));

  const total = items.reduce((sum, it) => {
    const amt = parseFloat(it.amount_dollars);
    const qty = parseInt(it.quantity, 10) || 1;
    return sum + (isNaN(amt) ? 0 : amt * qty);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Recipient email required");
    const cleanItems = items
      .filter((it) => it.description.trim() && parseFloat(it.amount_dollars) > 0)
      .map((it) => ({
        description: it.description.trim(),
        amount_dollars: parseFloat(it.amount_dollars),
        quantity: parseInt(it.quantity, 10) || 1,
      }));
    if (cleanItems.length === 0) return toast.error("Add at least one line item");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        recipient_email: email.trim().toLowerCase(),
        recipient_name: name.trim(),
        memo: memo.trim(),
        days_until_due: daysDue,
        line_items: cleanItems,
        send_immediately: true,
      });
      toast.success(`Invoice ${data.number} sent to ${data.recipient_email}`);
      try { await navigator.clipboard.writeText(data.hosted_invoice_url); toast.success("Payment link copied"); } catch (e) {}
      setShowForm(false);
      setEmail(""); setName(""); setMemo("");
      setItems([{ description: "", amount_dollars: "", quantity: 1 }]);
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setCreating(false); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const resend = async (id) => {
    try { await api.post(`/admin/invoices/${id}/resend`); toast.success("Resent"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const voidInv = async (id) => {
    if (!window.confirm("Void this invoice? The customer will no longer be able to pay it.")) return;
    try { await api.post(`/admin/invoices/${id}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const statusColor = {
    paid: "bg-sage-100 text-sage-800",
    open: "bg-ochre-100 text-ochre-800",
    draft: "bg-stone2-100 text-stone2-700",
    void: "bg-red-100 text-red-700",
    uncollectible: "bg-red-100 text-red-700",
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="admin-invoices-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back to admin
            </Link>
            <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-sage-600" /> Invoices
            </h1>
            <p className="mt-1 text-sm text-stone2-600">Send Stripe-hosted invoices. Money lands in your Stripe account.</p>
          </div>
          {!showForm && (
            <button type="button" onClick={() => setShowForm(true)}
              data-testid="admin-invoice-new"
              className="btn-primary">
              <Plus className="w-4 h-4" /> New invoice
            </button>
          )}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="paper-card p-5">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Collected</div>
            <div className="mt-1 text-3xl font-heading font-bold text-sage-900" data-testid="admin-invoice-total-collected">{dollars(totals.collected)}</div>
          </div>
          <div className="paper-card p-5">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-ochre-700">Outstanding</div>
            <div className="mt-1 text-3xl font-heading font-bold text-sage-900" data-testid="admin-invoice-total-outstanding">{dollars(totals.outstanding)}</div>
          </div>
        </div>

        {showForm && (
          <form onSubmit={submit} className="mt-8 paper-card p-6 space-y-4" data-testid="admin-invoice-form">
            <h2 className="font-heading font-bold text-sage-900 text-xl">Send an invoice</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Recipient email</label>
                <input required type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-invoice-email"
                  placeholder="parent@example.com"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
              <div>
                <label className="field-label">Recipient name <span className="text-stone2-400 font-normal">(optional)</span></label>
                <input type="text" value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="admin-invoice-name"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
            </div>

            <div>
              <label className="field-label">Line items</label>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_60px_40px] gap-2 items-start" data-testid={`admin-invoice-item-${i}`}>
                    <input type="text" placeholder="Description (e.g. Sitter service on Jan 15)"
                      value={it.description}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                    <input type="number" step="0.01" min="0" placeholder="$0.00"
                      value={it.amount_dollars}
                      onChange={(e) => updateItem(i, "amount_dollars", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                    <input type="number" min="1" placeholder="Qty"
                      value={it.quantity}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500 text-center" />
                    <button type="button" onClick={() => removeItem(i)} disabled={items.length === 1}
                      className="text-stone2-400 hover:text-red-600 disabled:opacity-30 p-2">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addItem}
                data-testid="admin-invoice-add-item"
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-sage-700 hover:text-sage-900">
                <Plus className="w-3.5 h-3.5" /> Add another line
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Days until due</label>
                <input type="number" min="0" max="90" value={daysDue}
                  onChange={(e) => setDaysDue(parseInt(e.target.value, 10) || 0)}
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
              </div>
              <div className="flex items-end">
                <div className="text-right w-full">
                  <div className="text-xs uppercase tracking-wider font-bold text-stone2-500">Total</div>
                  <div className="text-2xl font-heading font-bold text-sage-900" data-testid="admin-invoice-total">
                    ${total.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="field-label">Memo <span className="text-stone2-400 font-normal">(shown on invoice)</span></label>
              <textarea value={memo} rows={2} maxLength={300}
                onChange={(e) => setMemo(e.target.value)}
                data-testid="admin-invoice-memo"
                placeholder="Thanks for choosing AutismBabysitters!"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
            </div>

            <div className="flex items-center gap-2">
              <button type="submit" disabled={creating}
                data-testid="admin-invoice-submit"
                className="btn-primary">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send invoice
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-sage">Cancel</button>
              <p className="text-xs text-stone2-500 ml-auto">Stripe emails a hosted payment page to your recipient.</p>
            </div>
          </form>
        )}

        <div className="mt-8 paper-card overflow-hidden">
          <div className="px-5 py-3 border-b border-stone2-200 flex items-center justify-between">
            <div className="font-heading font-semibold text-sage-900">All invoices</div>
            <button type="button" onClick={load} className="text-xs text-sage-700 font-semibold inline-flex items-center gap-1 hover:text-sage-900">
              <RefreshCw className={`w-3 h-3 ${loadingList ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {invoices.length === 0 ? (
            <div className="p-10 text-center text-stone2-500 text-sm">
              No invoices yet. Click "New invoice" to send your first.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-invoices-table">
                <thead className="bg-sage-50 text-sage-900">
                  <tr>
                    <th className="text-left px-4 py-3 font-heading font-semibold">#</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Recipient</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Amount</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Sent</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone2-100">
                  {invoices.map((inv) => (
                    <tr key={inv.id} data-testid={`admin-invoice-row-${inv.id}`} className="hover:bg-sage-50/60">
                      <td className="px-4 py-3 font-mono text-xs text-stone2-600">{inv.number || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-sage-900">{inv.recipient_name || "—"}</div>
                        <div className="text-xs text-stone2-500">{inv.recipient_email}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-sage-900">
                        {dollars(inv.amount_due_cents)}
                        {inv.status === "paid" && inv.amount_paid_cents ? (
                          <div className="text-[10px] text-sage-700">paid {dollars(inv.amount_paid_cents)}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${statusColor[inv.status] || "bg-stone2-100"}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-stone2-500 whitespace-nowrap">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {inv.hosted_invoice_url && (
                            <>
                              <button type="button" onClick={() => copyLink(inv.hosted_invoice_url)}
                                data-testid={`admin-invoice-copy-${inv.id}`}
                                className="text-xs font-semibold px-2 py-1 rounded-md bg-sage-600 text-white hover:bg-sage-700 inline-flex items-center gap-1">
                                <Copy className="w-3 h-3" /> Link
                              </button>
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                                className="text-xs font-semibold px-2 py-1 rounded-md border border-stone2-200 text-sage-800 hover:bg-sage-50 inline-flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </>
                          )}
                          {inv.status === "open" && (
                            <>
                              <button type="button" onClick={() => resend(inv.id)}
                                data-testid={`admin-invoice-resend-${inv.id}`}
                                className="text-xs font-semibold px-2 py-1 rounded-md border border-stone2-200 text-sage-800 hover:bg-sage-50">
                                <Send className="w-3 h-3" />
                              </button>
                              <button type="button" onClick={() => voidInv(inv.id)}
                                data-testid={`admin-invoice-void-${inv.id}`}
                                className="text-xs font-semibold px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
